import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { PhotoIcon } from '@heroicons/react/24/outline'
import SettingsService from '@/services/SettingsService'
import { showError, showSuccess } from '@/lib/dialogHelper'

const CoverImageUpload = () => {
    const { t } = useTranslation()
    
    const baseUrl = window.RUNTIME_CONFIG.API_URL;
    const currentCover = `${baseUrl}/api/settings/getcover.jpg?t=${new Date().getTime()}`;

	const [coverFile, setCoverFile] = useState(null)
	const [coverPreview, setCoverPreview] = useState(null)
	const [uploadingCover, setUploadingCover] = useState(false)
	const [uploadProgress, setUploadProgress] = useState(0)
	const [removingCover, setRemovingCover] = useState(false)
	const [coverError, setCoverError] = useState(false)

	// Cleanup object URLs on unmount to prevent memory leaks
	useEffect(() => {
		return () => {
			if (coverPreview) {
				URL.revokeObjectURL(coverPreview)
			}
		}
	}, [coverPreview])

    useEffect(() => {
        // if coverFile is selected start the upload process
        if (coverFile) {
            handleCoverUpload();
        }
    }, [coverFile]);

	const handleCoverFileSelect = (e) => {
		const file = e.target.files[0]
		if (file) {
			// Validate file type
			if (file.type !== 'image/jpeg') {
				showError(t('settingsPage.system.cover.errors.invalidType'))
				e.target.value = '' // Reset the input
				return
			}

			// Validate file size (5MB max)
			if (file.size > 5000 * 1024) {
				showError(t('settingsPage.system.cover.errors.fileTooLarge'))
				e.target.value = '' // Reset the input
				return
			}

			setCoverFile(file)
			const previewUrl = URL.createObjectURL(file)
			setCoverPreview(previewUrl)

		}
	}

	const handleCoverUpload = async () => {
		if (!coverFile) {
			showError(t('settingsPage.system.cover.errors.selectFileFirst'))
			return
		}

		setUploadingCover(true)
		setUploadProgress(0)
		
		try {
			const onUploadProgress = (progressEvent) => {
				const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
				setUploadProgress(percentCompleted)
			}

			const { success, error, message } = await SettingsService.uploadCover(coverFile, onUploadProgress)
			
			if (error) {
				console.error("Failed to upload cover image:", error)
				showError(t('settingsPage.system.cover.errors.uploadFailed', { error }))
			} else if (success) {
				setUploadProgress(100)
				const successMessage = message || t('settingsPage.system.cover.messages.uploadSuccess')
				showSuccess(successMessage)
				// Clear the file input after successful upload
				setCoverFile(null)
				document.getElementById("cover-upload").value = ''
			}
		} catch (error) {
			console.error("Unexpected error uploading cover image:", error)
			const errorMessage = t('settingsPage.system.cover.errors.uploadUnexpected', { error: error.message })
			showError(errorMessage)
		} finally {
			setUploadingCover(false)
			setUploadProgress(0)
		}
	}

	const handleRemoveCover = async () => {
		setRemovingCover(true)
		
		try {
			const { success, error, message } = await SettingsService.removeCover()
			
			if (error) {
				console.error("Failed to remove cover:", error)
				showError(t('settingsPage.system.cover.errors.removeFailed', { error }))
			} else if (success) {
				const successMessage = message || t('settingsPage.system.cover.messages.removeSuccess')
				showSuccess(successMessage)
				// Reset cover error state to force a reload of the current cover
				setCoverError(false)
				// Force image refresh by updating the src
				const coverPreviewEl = document.getElementById("cover-preview")
				if (coverPreviewEl?.tagName === 'IMG') {
					coverPreviewEl.src = `${baseUrl}/api/settings/getcover.jpg?t=${new Date().getTime()}`
				}
			}
		} catch (error) {
			console.error("Unexpected error removing cover:", error)
			const errorMessage = t('settingsPage.system.cover.errors.removeUnexpected', { error: error.message })
			showError(errorMessage)
		} finally {
			setRemovingCover(false)
		}
	}

	return (
		<div className="col-span-full flex items-center gap-x-8">
			{coverError ? (
				<div
					id="cover-preview"
					aria-label={t('settingsPage.system.cover.alt')}
					className="flex size-24 flex-none items-center justify-center rounded-lg bg-gray-100 outline -outline-offset-1 outline-black/5"
				>
					<PhotoIcon className="size-10 text-gray-400" aria-hidden="true" />
				</div>
			) : (
				<img
					alt={t('settingsPage.system.cover.alt')}
					src={currentCover}
					onError={() => setCoverError(true)}
					className="size-24 flex-none rounded-lg bg-gray-100 object-cover outline -outline-offset-1 outline-black/5"
					id="cover-preview"
				/>
			)}
			<div>
				<div className="flex gap-2">
					<label htmlFor="cover-upload" className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring-1 inset-ring-gray-300 hover:bg-gray-100">
						{uploadingCover ? t('settingsPage.system.cover.actions.uploadingProgress', { progress: uploadProgress }) : t('settingsPage.system.cover.actions.change')}
					</label>
					<button
						type="button"
						onClick={handleRemoveCover}
						disabled={removingCover || uploadingCover || coverError}
						className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						{removingCover ? t('settingsPage.system.cover.actions.removing') : t('settingsPage.system.cover.actions.remove')}
					</button>
				</div>
				<input
					id="cover-upload"
					type="file"
					accept="image/jpeg"
					className="hidden"
					onChange={handleCoverFileSelect}
					disabled={uploadingCover || removingCover}
				/>
				<p className="mt-2 text-xs text-gray-500">{t('settingsPage.system.cover.hint')}</p>
				{coverFile && (
					<div className="mt-2">
						<button
							type="button"
							onClick={handleCoverUpload}
							disabled={uploadingCover || removingCover}
							className="block rounded-md bg-indigo-600 px-3 py-1 text-xs font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
						>
							{uploadingCover ? t('settingsPage.system.cover.actions.uploadingProgress', { progress: uploadProgress }) : t('settingsPage.system.cover.actions.upload')}
						</button>
						{uploadingCover && (
							<div className="mt-2">
								<div className="flex justify-between text-xs text-gray-600 mb-1">
									<span>{t('settingsPage.system.cover.actions.uploading')}</span>
									<span>{uploadProgress}%</span>
								</div>
								<div className="w-full bg-gray-200 rounded-full h-2">
									<div 
										className="bg-indigo-600 h-2 rounded-full transition-all duration-300 ease-out" 
										style={{ width: `${uploadProgress}%` }}
									></div>
								</div>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	)
}

export default CoverImageUpload
